import { useState, useEffect, useCallback, useMemo } from "react";
import {
  getTranslation,
  getLanguageTranslations,
  getBrowserLanguage,
  isLanguageSupported,
  SUPPORTED_LANGUAGES,
} from "../services/translationService";

const LANGUAGE_STORAGE_KEY = "slam_preferred_language";

/**
 * Hook for translation functionality
 * Provides translation function, language state, and language switching
 *
 * @returns {Object} Translation utilities
 * - language: Current language code
 * - setLanguage: Function to change language
 * - t: Translation function (key, fallback) => translated string
 * - translations: All translations for current language
 * - isTranslating: Loading state (for future async translations)
 * - supportedLanguages: Array of supported language objects
 */
const useTranslation = () => {
  // Initialize from localStorage or browser preference
  const [language, setLanguageState] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored && isLanguageSupported(stored)) {
        return stored;
      }
      return getBrowserLanguage();
    }
    return "en";
  });

  const [isTranslating, setIsTranslating] = useState(false);

  // Persist language preference
  const setLanguage = useCallback((newLanguage) => {
    if (isLanguageSupported(newLanguage)) {
      setLanguageState(newLanguage);
      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLanguage);
    } else {
      console.warn(`Language "${newLanguage}" is not supported`);
    }
  }, []);

  // Translation function
  const t = useCallback(
    (key, fallback = "") => {
      return getTranslation(key, language, fallback);
    },
    [language]
  );

  // Get all translations for current language
  const translations = useMemo(() => {
    return getLanguageTranslations(language);
  }, [language]);

  // Listen for language changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === LANGUAGE_STORAGE_KEY && e.newValue) {
        if (isLanguageSupported(e.newValue)) {
          setLanguageState(e.newValue);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  return {
    language,
    setLanguage,
    t,
    translations,
    isTranslating,
    supportedLanguages: SUPPORTED_LANGUAGES,
  };
};

export default useTranslation;
